import { useCallback } from "react";
import type { IMessage } from "react-native-gifted-chat";

import { closeActiveStream } from "../utils/activeStream";
import {
  generateSessionId,
  getUserId,
  getOrganizationId,
} from "../utils/authUtils";
import { useThreadListSync } from "./useThreadListSync";

import { useCurrentUser } from "@/features/auth";
import {
  useToggleThreadFavoriteMutation,
  useUpdateThreadStatusMutation,
  UserThread,
} from "@/generated/graphql";
import {
  selectedThreadIdVar,
  threadHistoryVar,
  threadSearchQueryVar,
  messagesVar,
  sessionIdVar,
  currentUserVar,
  currentStatusVar,
  agentProgressVar,
  currentProgressMessageIdVar,
  errorVar,
  isLoadingVar,
  connectionStatusVar,
} from "@/stores/knowledgeGraph";

/**
 * Custom hook for managing chat thread operations including favorite, delete, archive, and restore
 * Provides GraphQL mutation wrappers with optimistic updates and local state synchronization
 * Manages thread status changes and maintains thread history consistency
 * @remarks
 * This hook combines GraphQL mutation operations with reactive variable operations.
 * All operations now use Apollo reactive variables instead of Zustand.
 * @returns Thread operation functions for managing conversation threads
 */
export const useThreadOperations = () => {
  // Apollo mutations
  const [toggleThreadFavoriteMutation] = useToggleThreadFavoriteMutation();
  const [updateThreadStatusMutation] = useUpdateThreadStatusMutation();

  // Authoritative authenticated user. currentUserVar (set by updateAuthContext)
  // lags one effect cycle behind the Apollo `me` result, so a new thread created
  // on the same commit (e.g. the ?q= auto-send) would otherwise build a sessionId
  // with an empty org + fallback userId — which the agent rejects as 401
  // "Invalid session" (SE-5426/SE-5427). Prefer this resolved value.
  const currentUser = useCurrentUser();

  // SE-8865: New Chat refreshes the Conversations list, so the chat just left
  // appears once the server has written it.
  const { refreshThreadList } = useThreadListSync();

  // Toggle thread favorite status
  const toggleThreadFavorite = useCallback(
    async (threadId: string): Promise<boolean> => {
      const threadHistory = threadHistoryVar();
      const result = await toggleThreadFavoriteMutation({
        variables: { threadId },
        optimisticResponse: {
          toggleThreadFavorite: {
            __typename: "UserThread",
            id: threadId,
            isFavorite: !threadHistory.find(t => t.id === threadId)?.isFavorite,
            updatedAt: new Date().toISOString(),
          },
        },
      });

      // Update local thread history with fresh state to avoid clobbering concurrent updates
      if (result.data?.toggleThreadFavorite) {
        const currentThreadHistory = threadHistoryVar();
        const updatedThreads = currentThreadHistory.map(thread =>
          thread.id === threadId
            ? {
                ...thread,
                isFavorite: result.data!.toggleThreadFavorite.isFavorite,
                updatedAt: result.data!.toggleThreadFavorite.updatedAt,
              }
            : thread
        );
        threadHistoryVar(updatedThreads);
      }

      return result.data?.toggleThreadFavorite?.isFavorite ?? false;
    },
    [toggleThreadFavoriteMutation]
  );

  // Update thread status (for delete/archive operations)
  const updateThreadStatus = useCallback(
    async (threadId: string, status: string): Promise<UserThread | null> => {
      const result = await updateThreadStatusMutation({
        variables: { threadId, status },
      });

      // Update local thread history with fresh state to avoid clobbering concurrent updates
      if (result.data?.updateThreadStatus) {
        const currentThreadHistory = threadHistoryVar();

        // If thread is deleted or archived, remove it from the list
        if (status === "deleted" || status === "archived") {
          const updatedThreads = currentThreadHistory.filter(
            thread => thread.id !== threadId
          );
          threadHistoryVar(updatedThreads);
        } else {
          // Update the thread status
          const updatedThreads = currentThreadHistory.map(thread =>
            thread.id === threadId
              ? {
                  ...thread,
                  status: result.data!.updateThreadStatus.status,
                  updatedAt: result.data!.updateThreadStatus.updatedAt,
                }
              : thread
          );
          threadHistoryVar(updatedThreads);
        }
        return result.data.updateThreadStatus as UserThread;
      }

      return null;
    },
    [updateThreadStatusMutation]
  );

  // SE-8367: these three report success as `true`, and a thrown error was NOT
  // the only way they can fail. `updateThreadStatus` returns `null` whenever
  // the mutation resolves without data, and the Apollo auth link does exactly
  // that - a protected operation sent before the access token is available is
  // completed with no value and never throws (`ApolloClient.tsx`, the
  // "Drop unauthenticated requests for protected operations" branch). Returning
  // `true` there tells the caller the conversation is gone when nothing was
  // written, which closes the confirm dialog on a delete that did not happen.
  // Report the resolved thread, not merely the absence of an exception.

  // Delete a thread (sets status to "deleted")
  const deleteThread = useCallback(
    async (threadId: string): Promise<boolean> => {
      try {
        return (await updateThreadStatus(threadId, "deleted")) !== null;
      } catch (_error) {
        return false;
      }
    },
    [updateThreadStatus]
  );

  // Archive a thread (sets status to "archived")
  const archiveThread = useCallback(
    async (threadId: string): Promise<boolean> => {
      try {
        return (await updateThreadStatus(threadId, "archived")) !== null;
      } catch (_error) {
        return false;
      }
    },
    [updateThreadStatus]
  );

  // Restore a thread (sets status to "active")
  const restoreThread = useCallback(
    async (threadId: string): Promise<boolean> => {
      try {
        return (await updateThreadStatus(threadId, "active")) !== null;
      } catch (_error) {
        return false;
      }
    },
    [updateThreadStatus]
  );

  /**
   * Set the currently selected thread ID.
   * @param threadId - Thread ID or null to clear selection
   */
  const setSelectedThreadId = useCallback((threadId: string | null) => {
    selectedThreadIdVar(threadId);
  }, []);

  /**
   * Update the thread history array.
   * @param threads - New thread history array
   */
  const updateThreadHistory = useCallback((threads: UserThread[]) => {
    threadHistoryVar(threads);
  }, []);

  /**
   * Set the thread search query.
   * @param query - Search query string
   */
  const setThreadSearchQuery = useCallback((query: string) => {
    threadSearchQueryVar(query);
  }, []);

  /**
   * Load messages from a thread into the chat
   * @param messages - Array of messages to load
   */
  const loadThreadMessages = useCallback((messages: IMessage[]) => {
    messagesVar(messages);
    errorVar(null);
    isLoadingVar(false);
  }, []);

  /**
   * Create a new thread by generating new session ID and clearing state
   * @remarks Returns async Promise to support platform-aware storage
   */
  const createNewThread = useCallback(async () => {
    // Prefer the resolved Apollo user; fall back to the reactive var only when
    // it has not resolved yet (keeps behaviour for non-React callers).
    const user = currentUser ?? currentUserVar();
    const userId = await getUserId(user);
    const orgId = getOrganizationId(user);
    const newSessionId = generateSessionId(orgId, userId);

    console.log("Thread Operations: Creating new thread", {
      newSessionId,
      userId,
      organizationId: orgId,
    });

    // SE-6900: actually abort the answer still streaming, rather than raising a
    // `shouldResetConnectionVar` flag that nothing ever read. Without this the
    // abandoned stream keeps appending its remaining tokens into the thread we
    // just cleared, so the new conversation opens showing the previous
    // question's answer.
    closeActiveStream();
    connectionStatusVar("disconnected");

    messagesVar([]);
    sessionIdVar(newSessionId);
    selectedThreadIdVar(null);
    currentStatusVar(null);
    agentProgressVar([]);
    currentProgressMessageIdVar(null);
    errorVar(null);
    isLoadingVar(false);
    void refreshThreadList();
  }, [currentUser, refreshThreadList]);

  return {
    // GraphQL mutation actions
    toggleThreadFavorite,
    deleteThread,
    archiveThread,
    restoreThread,
    updateThreadStatus,
    // Reactive variable actions
    setSelectedThreadId,
    updateThreadHistory,
    setThreadSearchQuery,
    loadThreadMessages,
    createNewThread,
  };
};
